/**
 * GanttChartControl
 *
 * Gantt chart for project timeline visualization
 * Shows tasks, durations, and dependencies
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

interface GanttTask {
  id: string;
  name: string;
  start: number;
  duration: number;
  progress: number;
  color: string;
}

const GanttChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const ganttConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      nameKey: (config?.nameKey as string) || 'name',
      startKey: (config?.startKey as string) || 'start',
      durationKey: (config?.durationKey as string) || 'duration',
      progressKey: (config?.progressKey as string) || 'progress',
    };
  }, [control.config]);

  const renderGantt = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract tasks
    const tasks: GanttTask[] = [];
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c',
      '#d084d0', '#13c2c2', '#f5ad14', '#eb2f96', '#52c41a',
    ];

    let maxTime = 0;
    chartData.forEach((d, idx) => {
      const name = String((d as Record<string, unknown>)[ganttConfig.nameKey] || `Task ${idx}`);
      const start = (d as Record<string, unknown>)[ganttConfig.startKey] as number | undefined;
      const duration = (d as Record<string, unknown>)[ganttConfig.durationKey] as number | undefined;
      const progress = (d as Record<string, unknown>)[ganttConfig.progressKey] as number | undefined;

      if (typeof start === 'number' && typeof duration === 'number' && duration > 0) {
        tasks.push({
          id: `task-${idx}`,
          name,
          start,
          duration,
          progress: typeof progress === 'number' ? Math.max(0, Math.min(1, progress)) : 0,
          color: colors[idx % colors.length],
        });

        maxTime = Math.max(maxTime, start + duration);
      }
    });

    if (tasks.length === 0) return null;

    const taskBarHeight = 30;
    const padding = 50;
    const nameColumnWidth = 120;
    const width = 600;
    const height = (control.config?.height as number) || 400;

    const timelineWidth = width - padding - nameColumnWidth;
    const timelineHeight = height - 2 * padding;
    const timeScale = timelineWidth / Math.max(maxTime, 1);

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Header */}
        <text
          x={padding + nameColumnWidth / 2}
          y={padding - 20}
          textAnchor="middle"
          fontSize="12"
          fontWeight="bold"
          fill="#374151"
        >
          Timeline
        </text>

        {/* Timeline grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const x = padding + nameColumnWidth + ratio * timelineWidth;
          return (
            <g key={`grid-${ratio}`}>
              <line
                x1={x}
                y1={padding}
                x2={x}
                y2={padding + timelineHeight}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
              <text
                x={x}
                y={padding - 5}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {(ratio * maxTime).toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Horizontal grid lines */}
        {tasks.map((_, idx) => {
          const y = padding + idx * taskBarHeight;
          return (
            <line
              key={`h-grid-${idx}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#f3f4f6"
              strokeWidth="1"
            />
          );
        })}

        {/* Task names */}
        {tasks.map((task, idx) => {
          const y = padding + idx * taskBarHeight + taskBarHeight / 2;
          return (
            <text
              key={`name-${task.id}`}
              x={padding + 5}
              y={y + 5}
              fontSize="12"
              fill="#374151"
            >
              {task.name.substring(0, 12)}
            </text>
          );
        })}

        {/* Task bars */}
        {tasks.map((task, idx) => {
          const y = padding + idx * taskBarHeight + 5;
          const barX = padding + nameColumnWidth + task.start * timeScale;
          const barWidth = task.duration * timeScale;

          return (
            <g key={`task-${task.id}`}>
              {/* Background bar */}
              <rect
                x={barX}
                y={y}
                width={Math.max(barWidth, 0)}
                height={taskBarHeight - 10}
                fill={task.color}
                opacity="0.3"
                stroke={task.color}
                strokeWidth="1"
              />

              {/* Progress bar */}
              {task.progress > 0 && (
                <rect
                  x={barX}
                  y={y}
                  width={Math.max(barWidth * task.progress, 0)}
                  height={taskBarHeight - 10}
                  fill={task.color}
                  opacity="0.9"
                />
              )}

              {/* Progress label */}
              {barWidth > 40 && (
                <text
                  x={barX + barWidth / 2}
                  y={y + taskBarHeight / 2}
                  textAnchor="middle"
                  fontSize="10"
                  fill={task.progress > 0.5 ? 'white' : '#374151'}
                  fontWeight="bold"
                >
                  {Math.round(task.progress * 100)}%
                </text>
              )}
            </g>
          );
        })}

        {/* Legend */}
        <g>
          <rect
            x={padding + nameColumnWidth}
            y={height - padding + 10}
            width={12}
            height={12}
            fill="#888"
            opacity="0.3"
          />
          <text
            x={padding + nameColumnWidth + 18}
            y={height - padding + 20}
            fontSize="11"
            fill="#6b7280"
          >
            Planned
          </text>

          <rect
            x={padding + nameColumnWidth + 80}
            y={height - padding + 10}
            width={12}
            height={12}
            fill="#888"
          />
          <text
            x={padding + nameColumnWidth + 98}
            y={height - padding + 20}
            fontSize="11"
            fill="#6b7280"
          >
            Completed
          </text>
        </g>
      </svg>
    );
  }, [chartData, ganttConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="gantt-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderGantt}</div>
    </BaseChartComponent>
  );
});

GanttChartControl.displayName = 'GanttChartControl';

export default GanttChartControl;
